// §V2 EXACT PORT — Learn (docs/design-spikes/pomela-platform-v2/index.html
// lines 709-773): search cmd (now a REAL field), the sanctioned gradhero
// moment, the steps4 "How Pomela works" path, the grid-2e topic cards with
// REAL derived counts, and a curated article list — all wired to the REAL
// Guide knowledge base (screens/Guide.tsx's exact content model + i18n
// keys, the honesty baseline for what Pomela truly supports).
//
// HONESTY NOTES (same discipline as Guide.tsx):
//  • Search is a REAL client-side filter over the on-page content (the
//    exact entryText() semantics Guide.tsx uses). Empty results route to
//    Pomy. The spike's .state-switch prototype toggler is dropped — the
//    statehost states are driven by the real filter.
//  • Topic counts are DERIVED from the real content arrays (they resolve
//    to the spike's 6/4/7/3 because the spike was drawn from this content).
//  • Category cards + article rows scroll to the REAL guide content
//    rendered on this screen (the spike's fake "Opening guide…" toasts had
//    nowhere to go).
//  • "Ask Pomy" opens the shell's REAL Pomy panel (/v1/copilot/chat) via
//    the pomela:open-pomy event; the hero CTA is the real composer flow.
//  • No fake popularity data: the curated list is titled honestly and its
//    rows are real articles with blurbs derived from their real body copy.
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Ic, tt } from './icons';
import { t } from '../lib/i18n';
import { goNav } from '../lib/nav-items';

// ─── the REAL guide content model — mirrors screens/Guide.tsx SECTIONS
//     (module-private there, so the skeleton is mirrored; every string
//     still resolves through the same lib/i18n.ts g.* catalog, which stays
//     the single source of content truth). Icons are the spike's {i:name}
//     vocabulary (closest MAP name where the legacy Lucide glyph has no
//     spike twin).

type Blk =
  | { k: 'p'; key: string; html?: boolean }
  | { k: 'b'; ic: string; key: string }
  | { k: 'cmp'; bad?: string; good: string }
  | { k: 'ex'; keys: string[] };

interface EntryDef {
  ic: string;
  titleKey: string;
  htmlTitle?: boolean;
  blocks: Blk[];
}

interface SectionDef {
  id: string;
  eyebrowKey: string;
  titleKey: string;
  ledeKey: string;
  tile: string;
  entries: EntryDef[];
}

const SECTIONS: SectionDef[] = [
  {
    id: 'guide-build',
    eyebrowKey: 'g.s1e',
    titleKey: 'g.s1t',
    ledeKey: 'g.s1l',
    tile: 'spark',
    entries: [
      { ic: 'cal', titleKey: 'g.a1t', htmlTitle: true, blocks: [{ k: 'p', key: 'g.a1b', html: true }, { k: 'ex', keys: ['g.a1e1', 'g.a1e2', 'g.a1e3'] }] },
      { ic: 'card', titleKey: 'g.a2t', htmlTitle: true, blocks: [{ k: 'p', key: 'g.a2b', html: true }, { k: 'ex', keys: ['g.a2e1', 'g.a2e2', 'g.a2e3'] }] },
      { ic: 'chart', titleKey: 'g.a3t', htmlTitle: true, blocks: [{ k: 'p', key: 'g.a3b', html: true }, { k: 'ex', keys: ['g.a3e1', 'g.a3e2', 'g.a3e3'] }] },
      { ic: 'book', titleKey: 'g.a4t', htmlTitle: true, blocks: [{ k: 'p', key: 'g.a4b', html: true }, { k: 'ex', keys: ['g.a4e1', 'g.a4e2', 'g.a4e3'] }] },
      { ic: 'compass', titleKey: 'g.a5t', htmlTitle: true, blocks: [{ k: 'p', key: 'g.a5b', html: true }, { k: 'ex', keys: ['g.a5e1', 'g.a5e2', 'g.a5e3'] }] },
      { ic: 'wrench', titleKey: 'g.a6t', htmlTitle: true, blocks: [{ k: 'p', key: 'g.a6b', html: true }, { k: 'ex', keys: ['g.a6e1', 'g.a6e2', 'g.a6e3'] }] }
    ]
  },
  {
    id: 'guide-results',
    eyebrowKey: 'g.s2e',
    titleKey: 'g.s2t',
    ledeKey: 'g.s2l',
    tile: 'target',
    entries: [
      { ic: 'target', titleKey: 'g.c1t', blocks: [{ k: 'p', key: 'g.c1p' }, { k: 'cmp', bad: 'g.c1bad', good: 'g.c1good' }] },
      { ic: 'phone', titleKey: 'g.c2t', blocks: [{ k: 'p', key: 'g.c2p' }, { k: 'b', ic: 'check', key: 'g.c2b1' }, { k: 'b', ic: 'check', key: 'g.c2b2' }] },
      { ic: 'x', titleKey: 'g.c3t', blocks: [{ k: 'p', key: 'g.c3p' }, { k: 'b', ic: 'pencil', key: 'g.c3b1' }, { k: 'b', ic: 'pencil', key: 'g.c3b2' }, { k: 'b', ic: 'pencil', key: 'g.c3b3' }] },
      { ic: 'wand', titleKey: 'g.c4t', blocks: [{ k: 'p', key: 'g.c4p' }, { k: 'b', ic: 'wand', key: 'g.c4b1' }, { k: 'b', ic: 'spark', key: 'g.c4b2' }] }
    ]
  },
  {
    id: 'guide-max',
    eyebrowKey: 'g.s3e',
    titleKey: 'g.s3t',
    ledeKey: 'g.s3l',
    tile: 'bolt',
    entries: [
      { ic: 'wand', titleKey: 'g.c5t', blocks: [{ k: 'p', key: 'g.c5p' }, { k: 'b', ic: 'check', key: 'g.c5b1' }] },
      { ic: 'phone', titleKey: 'g.c6t', blocks: [{ k: 'p', key: 'g.c6p' }, { k: 'b', ic: 'plus', key: 'g.c6b1' }, { k: 'b', ic: 'phone', key: 'g.c6b2' }, { k: 'b', ic: 'bell', key: 'g.c6b3' }] },
      { ic: 'card', titleKey: 'g.c7t', blocks: [{ k: 'p', key: 'g.c7p' }] },
      { ic: 'bolt', titleKey: 'g.c8t', blocks: [{ k: 'p', key: 'g.c8p' }] },
      { ic: 'lock', titleKey: 'g.c9t', blocks: [{ k: 'p', key: 'g.c9p' }, { k: 'b', ic: 'check', key: 'g.c9b1' }, { k: 'b', ic: 'check', key: 'g.c9b2' }] },
      { ic: 'download', titleKey: 'g.c10t', blocks: [{ k: 'p', key: 'g.c10p' }, { k: 'b', ic: 'ext', key: 'g.c10b1' }, { k: 'b', ic: 'download', key: 'g.c10b2' }] },
      { ic: 'compass', titleKey: 'g.c11t', blocks: [{ k: 'p', key: 'g.c11p' }, { k: 'b', ic: 'globe', key: 'g.c11b1' }, { k: 'b', ic: 'copy', key: 'g.c11b2' }, { k: 'b', ic: 'share', key: 'g.c11b3' }] }
    ]
  },
  {
    id: 'guide-patterns',
    eyebrowKey: 'g.s4e',
    titleKey: 'g.s4t',
    ledeKey: 'g.s4l',
    tile: 'bulb',
    entries: [
      { ic: 'bulb', titleKey: 'g.c12t', blocks: [{ k: 'b', ic: 'check', key: 'g.c12b1' }, { k: 'b', ic: 'check', key: 'g.c12b2' }, { k: 'b', ic: 'check', key: 'g.c12b3' }, { k: 'b', ic: 'check', key: 'g.c12b4' }] },
      { ic: 'x', titleKey: 'g.c13t', blocks: [{ k: 'b', ic: 'pencil', key: 'g.c13b1' }, { k: 'b', ic: 'pencil', key: 'g.c13b2' }, { k: 'b', ic: 'pencil', key: 'g.c13b3' }] },
      { ic: 'rocket', titleKey: 'g.c14t', blocks: [{ k: 'p', key: 'g.c14p' }, { k: 'cmp', good: 'g.c14good' }, { k: 'p', key: 'g.c14foot' }] }
    ]
  }
];

/** The spike's 4 "how it works" steps — titles are the legacy catalog's
 *  exact strings; subtitles are the spike's exact copy. */
const STEPS = [
  { n: '1', ic: 'pencil', tKey: 'g.step1t', sKey: 'v2.learn.step1s', en: 'Say what you want in a sentence.' },
  { n: '2', ic: 'spark', tKey: 'g.step2t', sKey: 'v2.learn.step2s', en: 'Pomela builds real screens + data.' },
  { n: '3', ic: 'wand', tKey: 'g.step3t', sKey: 'v2.learn.step3s', en: 'Chat or tap to refine anything.' },
  { n: '4', ic: 'phone', tKey: 'g.step4t', sKey: 'v2.learn.step4s', en: 'Install & share in a tap.' }
] as const;

/** Curated article rows (the spike's list slot) → REAL guide entries; the
 *  blurb is derived from each article's real body copy, not marketing. */
const CURATED = [
  { ic: 'cal', titleKey: 'g.a1t', bodyKey: 'g.a1b' },
  { ic: 'coin', titleKey: 'g.c7t', bodyKey: 'g.c7p' },
  { ic: 'globe', titleKey: 'g.c11t', bodyKey: 'g.c11p' }
] as const;

// ─── helpers (Guide.tsx's exact search semantics) ───────────────────────────

const strip = (s: string): string => s.replace(/<[^>]*>/g, ' ');

/** All searchable text for an entry, flattened + lowercased. */
function entryText(e: EntryDef): string {
  const parts: string[] = [strip(t(e.titleKey))];
  for (const b of e.blocks) {
    if (b.k === 'p') parts.push(strip(t(b.key)));
    else if (b.k === 'b') parts.push(t(b.key));
    else if (b.k === 'cmp') {
      if (b.bad) parts.push(t(b.bad));
      parts.push(t(b.good));
    } else parts.push(...b.keys.map((k) => t(k)));
  }
  return parts.join(' ').toLowerCase();
}

/** Honest truncation for curated-row blurbs (real body copy, shortened). */
function truncate(s: string, max = 90): string {
  const clean = s.trim().replace(/\s+/g, ' ');
  return clean.length <= max ? clean : `${clean.slice(0, max - 1).trimEnd()}…`;
}

const secId = (s: SectionDef): string => `v2-${s.id}`;
const entryAnchor = (titleKey: string): string => `v2-guide-${titleKey.replace(/\./g, '-')}`;

function jumpTo(id: string): void {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function openPomy(): void {
  window.dispatchEvent(new CustomEvent('pomela:open-pomy'));
}

export function LearnV2(): JSX.Element {
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLElement>(null);
  const q = query.trim().toLowerCase();
  const searching = q.length > 0;

  // REAL client-side filter over the real articles (legacy Guide's flow).
  const results = useMemo<EntryDef[] | null>(() => {
    if (!q) return null;
    return SECTIONS.flatMap((s) => s.entries.filter((e) => entryText(e).includes(q)));
  }, [q]);

  const state: 'loaded' | 'empty' = searching && results !== null && results.length === 0 ? 'empty' : 'loaded';

  // Scroll-reveal — the spike's exact observer (root = #canvas, .12 / -6%).
  // The three .sr blocks (hero / steps / browse) are mounted once, so one run.
  useEffect(() => {
    const rootEl = rootRef.current;
    if (!rootEl) return;
    if (!('IntersectionObserver' in window)) {
      rootEl.querySelectorAll('.sr,.sr-stagger').forEach((el) => el.classList.add('in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('in');
            io.unobserve(en.target);
          }
        });
      },
      { root: document.getElementById('canvas'), threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );
    const raf = requestAnimationFrame(() => {
      rootEl.querySelectorAll('.sr:not(.in),.sr-stagger:not(.in)').forEach((el) => io.observe(el));
    });
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
    };
  }, []);

  return (
    <section className="screen on" data-screen="learn" ref={rootRef}>
      <div className="wrap stack">
        <div className="screen-head">
          <div className="sh-grow">
            {/* the spike's static .cmd button → a REAL search field over the
                on-page guide content (same classes, same look) */}
            <label className="cmd" style="max-width:480px;width:100%;cursor:text">
              <Ic n="search" />
              <input
                className="cmd-ph"
                type="search"
                value={query}
                placeholder={t('g.searchPh')}
                aria-label={t('g.searchPh')}
                onInput={(e) => setQuery((e.currentTarget as HTMLInputElement).value)}
                style="background:none;border:0;outline:none;color:var(--fg);font-family:inherit;min-width:0;padding:0"
              />
            </label>
          </div>
          {/* data-pomy-open="general" → the shell's REAL Pomy panel */}
          <button className="btn btn-ghost" type="button" onClick={openPomy}>
            <Ic n="spark" /> {t('g.askPomy')}
          </button>
        </div>

        {/* gradhero — the sanctioned gradient moment; CTA = real composer flow */}
        <div className="gradhero sr">
          <span className="gh-ey">{t('g.heroBadge')}</span>
          <h3>{t('guide.heroTitle')}</h3>
          <p>
            {tt(
              'v2.learn.heroSub',
              'Pomela turns one sentence into a complete, installable mobile app: real screens, real interactions, real data. This is the field guide, what to build, how to phrase it, and the tricks that make an app go from good to genuinely yours.'
            )}
          </p>
          <div className="gh-link">
            <button
              className="btn btn-solid"
              type="button"
              onClick={() => {
                // the spike's data-go="home" data-focus-composer → the real
                // "fresh composer" flow (same event the CommandPalette fires)
                window.dispatchEvent(new CustomEvent('pomela:new-project'));
                goNav('');
              }}
            >
              <Ic n="rocket" /> {t('g.heroCta')}
            </button>
          </div>
        </div>

        {/* steps4 — the spike's 4 "how it works" steps, exact copy */}
        <div className="sr">
          <span className="sec-label" style="display:block;margin-bottom:14px">{t('g.howTitle')}</span>
          <div className="steps4">
            {STEPS.map((s) => (
              <div className="step" key={s.tKey}>
                <span className="st-n">{s.n}</span>
                <span className="st-ic"><Ic n={s.ic} /></span>
                <b>{t(s.tKey)}</b>
                <span>{tt(s.sKey, s.en)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="sr">
          <div className="row" style="justify-content:space-between;margin-bottom:14px">
            <span className="sec-label">{t('g.browseTitle')}</span>
            {/* the spike's .state-switch is prototype-only — the statehost
                states are driven by the real search filter */}
          </div>

          <div data-statehost="learn" data-state={state}>
            <div data-when="loaded" className="stack">
              {searching ? (
                // REAL search results — the matching guide articles, in full
                (results ?? []).map((e) => <EntryCard key={e.titleKey} entry={e} />)
              ) : (
                <>
                  {/* grid-2e topic cards — REAL derived counts, real anchors */}
                  <div className="grid-2e">
                    {SECTIONS.map((s) => (
                      <button key={s.id} className="panel lrow clk" type="button" onClick={() => jumpTo(secId(s))}>
                        <span className="l-ico brand"><Ic n={s.tile} /></span>
                        <div className="l-txt">
                          <b>{t(s.titleKey)}</b>
                          <span>{`${s.entries.length} ${t('g.countSuffix')}`}</span>
                        </div>
                        <span className="l-trail"><Ic n="chev" /></span>
                      </button>
                    ))}
                  </div>

                  {/* curated article rows — REAL guide entries, real blurbs */}
                  <div className="panel">
                    <div className="panel-hd"><h3>{tt('v2.learn.curatedT', 'Start with these')}</h3></div>
                    <div className="list divide">
                      {CURATED.map((c) => (
                        <button key={c.titleKey} className="lrow clk" type="button" onClick={() => jumpTo(entryAnchor(c.titleKey))}>
                          <span className="l-ico"><Ic n={c.ic} /></span>
                          <div className="l-txt">
                            <b>{t(c.titleKey)}</b>
                            <span>{truncate(strip(t(c.bodyKey)))}</span>
                          </div>
                          <span className="l-trail"><Ic n="chev" /></span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* the REAL guide content — the anchors the cards + rows and
                      the search filter point at (legacy Guide.tsx's exact
                      catalog content, rendered in the spike's vocabulary) */}
                  {SECTIONS.map((s) => (
                    <section key={s.id} id={secId(s)} className="stack" style="gap:12px;scroll-margin-top:12px">
                      <div>
                        <span className="sec-label" style="display:block;margin-bottom:8px">{t(s.eyebrowKey)}</span>
                        <h3 style="font-size:20px;letter-spacing:-.02em;margin:0 0 6px">{t(s.titleKey)}</h3>
                        <p className="sub-h" style="margin:0;max-width:60ch">{t(s.ledeKey)}</p>
                      </div>
                      {s.entries.map((e) => (
                        <EntryCard key={e.titleKey} entry={e} />
                      ))}
                      {s.id === 'guide-build' && (
                        <p className="help" style="font-style:italic;font-size:13px;line-height:1.6;margin:0">{t('g.archFoot')}</p>
                      )}
                    </section>
                  ))}
                </>
              )}
            </div>

            {/* NO RESULTS — the spike's empty state, driven by the real filter */}
            {state === 'empty' && (
              <div data-when="empty">
                <div className="panel empty">
                  <span className="e-ic"><Ic n="search" /></span>
                  <h4>{tt('v2.learn.noResT', 'No guides match that yet')}</h4>
                  <p>{tt('v2.learn.noResB', "We couldn't find a guide for that search. Try a broader word, or ask Pomy, it knows the whole product inside out.")}</p>
                  <button className="btn btn-primary" type="button" onClick={openPomy}>
                    <Ic n="spark" /> {t('g.askResults')}
                  </button>
                  <button className="link" type="button" style="margin-top:12px" onClick={() => setQuery('')}>
                    <Ic n="arrowl" /> {t('g.clearSearch')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── the real article card (Guide.tsx's content blocks in spike classes) ────

function EntryCard({ entry }: { entry: EntryDef }): JSX.Element {
  return (
    <article className="panel panel-p" id={entryAnchor(entry.titleKey)} style="scroll-margin-top:12px">
      <div className="row" style="gap:10px;margin-bottom:10px">
        <span className="l-ico brand" style="width:32px;height:32px;border-radius:10px"><Ic n={entry.ic} size={15} /></span>
        {entry.htmlTitle ? (
          <h4 style="font-size:15.5px;flex:1;min-width:0" dangerouslySetInnerHTML={{ __html: t(entry.titleKey) }} />
        ) : (
          <h4 style="font-size:15.5px;flex:1;min-width:0">{t(entry.titleKey)}</h4>
        )}
      </div>
      {entry.blocks.map((b, i) => (
        <Blck key={i} b={b} />
      ))}
    </article>
  );
}

function Blck({ b }: { b: Blk }): JSX.Element {
  if (b.k === 'p') {
    const st = 'font-size:13.5px;line-height:1.6;color:var(--muted);margin:0 0 10px';
    return b.html ? <p style={st} dangerouslySetInnerHTML={{ __html: t(b.key) }} /> : <p style={st}>{t(b.key)}</p>;
  }
  if (b.k === 'b') {
    return (
      <div className="row" style="align-items:flex-start;gap:10px;padding:5px 0">
        <span className="l-ico" style="width:22px;height:22px;border-radius:7px;margin-top:1px"><Ic n={b.ic} size={12} /></span>
        <span style="font-size:13px;line-height:1.55;color:var(--muted)">{t(b.key)}</span>
      </div>
    );
  }
  if (b.k === 'ex') {
    return (
      <div className="stack" style="gap:7px;margin-top:4px;padding-top:12px;border-top:1px solid var(--hair)">
        {b.keys.map((k) => (
          <p key={k} className="row" style="align-items:flex-start;gap:9px;margin:0;font-size:12.5px;line-height:1.5;font-style:italic">
            <span className="l-ico brand" style="width:18px;height:18px;border-radius:6px;margin-top:1px"><Ic n="spark" size={11} /></span>
            <span>{t(k)}</span>
          </p>
        ))}
      </div>
    );
  }
  // cmp — the before/after prompt comparison (Vague vs Specific)
  return (
    <div className="stack" style="gap:8px;margin-top:4px">
      {b.bad && (
        <div className="banner error">
          <span className="b-ic"><Ic n="x" /></span>
          <div>
            <b>{t('g.compareBad')}</b>
            <p style="font-style:italic">{t(b.bad)}</p>
          </div>
        </div>
      )}
      <div className="banner ok">
        <span className="b-ic"><Ic n="check" /></span>
        <div>
          <b>{t('g.compareGood')}</b>
          <p style="font-style:italic">{t(b.good)}</p>
        </div>
      </div>
    </div>
  );
}
